import React, { useState, useEffect } from 'react'
import { notificationService, Notification } from '@services/notificationService'
import { Bell, Check, CheckCheck, Clock, AlertCircle, FileText, DollarSign, AlertTriangle } from 'lucide-react'
import { toast } from 'react-hot-toast'

const NotificationsPage: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(true)
  const [unreadCount, setUnreadCount] = useState(0)
  const [filter, setFilter] = useState<'all' | 'unread'>('all')

  useEffect(() => {
    loadNotifications()
  }, [filter])

  const loadNotifications = async () => {
    try {
      setLoading(true)
      const response = await notificationService.getNotifications({
        limit: 50,
        unreadOnly: filter === 'unread'
      })
      setNotifications(response.notifications)
      setUnreadCount(response.unreadCount)
    } catch (error) {
      console.error('Error loading notifications:', error)
      toast.error('載入通知失敗')
    } finally {
      setLoading(false)
    }
  }

  const markAsRead = async (notificationId: string) => {
    try {
      await notificationService.markAsRead(notificationId)
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, isRead: true }
            : notification
        )
      )
      setUnreadCount(prev => Math.max(0, prev - 1))
      toast.success('已標記為已讀')
    } catch (error) {
      console.error('Error marking notification as read:', error)
      toast.error('標記為已讀失敗')
    }
  }

  const markAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead()
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, isRead: true }))
      )
      setUnreadCount(0)
      toast.success('已標記所有通知為已讀')
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
      toast.error('標記所有通知為已讀失敗')
    }
  }

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'TIMESHEET_REMINDER':
      case 'TIMESHEET_OVERDUE':
      case 'TIMESHEET_DEADLINE_APPROACHING':
        return <Clock className="w-5 h-5 text-blue-500" />
      case 'TIMESHEET_SUBMITTED':
      case 'TIMESHEET_APPROVED':
        return <Check className="w-5 h-5 text-green-500" />
      case 'TIMESHEET_REJECTED':
        return <AlertCircle className="w-5 h-5 text-red-500" />
      case 'BUDGET_WARNING':
      case 'BUDGET_EXCEEDED':
        return <DollarSign className="w-5 h-5 text-yellow-500" />
      case 'WORK_TIME_WARNING':
        return <AlertTriangle className="w-5 h-5 text-orange-500" />
      default:
        return <FileText className="w-5 h-5 text-gray-500" />
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))
    
    if (diffInHours < 1) {
      return '剛剛'
    } else if (diffInHours < 24) {
      return `${diffInHours} 小時前`
    } else if (diffInHours < 168) {
      return `${Math.floor(diffInHours / 24)} 天前`
    } else {
      return date.toLocaleDateString('zh-TW', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }
  }

  const filteredNotifications = notifications.filter(notification => 
    filter === 'all' || !notification.isRead
  )

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-soft border border-primary-200 overflow-hidden">
        <div className="px-6 py-4 bg-primary-50 border-b border-primary-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Bell className="w-6 h-6 text-primary-600" />
              <div>
                <h1 className="text-xl font-semibold text-secondary-800">通知中心</h1>
                <p className="text-sm text-secondary-600">
                  {unreadCount > 0 ? `${unreadCount} 則未讀通知` : '沒有未讀通知'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <div className="flex bg-white rounded-lg border border-primary-200 overflow-hidden">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    filter === 'all'
                      ? 'bg-primary-600 text-white'
                      : 'text-secondary-700 hover:bg-primary-50'
                  }`}
                >
                  全部
                </button>
                <button
                  onClick={() => setFilter('unread')}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    filter === 'unread'
                      ? 'bg-primary-600 text-white'
                      : 'text-secondary-700 hover:bg-primary-50'
                  }`}
                >
                  未讀
                </button>
              </div>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="btn-ghost text-sm"
                >
                  <CheckCheck className="w-4 h-4 mr-1" />
                  全部標記為已讀
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="divide-y divide-primary-100">
          {filteredNotifications.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <Bell className="w-12 h-12 text-secondary-300 mx-auto mb-4" />
              <p className="text-secondary-600">
                {filter === 'unread' ? '沒有未讀通知' : '沒有通知'}
              </p>
            </div>
          ) : (
            filteredNotifications.map((notification) => (
              <div
                key={notification.id}
                className={`px-6 py-4 hover:bg-primary-25 transition-colors ${
                  !notification.isRead ? 'bg-primary-25' : ''
                }`}
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 mt-1">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className={`text-sm font-medium ${
                          !notification.isRead ? 'text-secondary-900' : 'text-secondary-700'
                        }`}>
                          {notification.title}
                        </h3>
                        <p className="mt-1 text-sm text-secondary-600">
                          {notification.message}
                        </p>
                        <p className="mt-2 text-xs text-secondary-500">
                          {formatDate(notification.createdAt)}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        {!notification.isRead && (
                          <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                        )}
                        {!notification.isRead && (
                          <button
                            onClick={() => markAsRead(notification.id)}
                            className="text-xs text-primary-600 hover:text-primary-700 transition-colors"
                          >
                            標記為已讀
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

export default NotificationsPage
